/** @jsx jsx */
/** @jsxRuntime classic */

import {jsx} from "@emotion/core";
import api from "api";
import css from "@emotion/css/macro";
import React, {Fragment, MutableRefObject, useCallback, useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import styles from "./Print.module.scss";
import Card from "components/Cards/Card";
import SummaryCard from "components/Cards/SummaryCard";
import {Dropdown, SelectOption} from "components/ui/Dropdown";
import {endpoints} from "config";
import {Spinner} from "components/ui/Spinner";
import {selectPortalConfig, selectPortalData} from "reducers/configuration";
import {selectClientLogoLink, selectDatabaseName} from "reducers/client";
import DataTable, {OptionRowTable} from "components/DataTable";
import {KeyQuestions, SummaryOfReviews, TrendScores} from "components/Charts";
import {parse as parseQueryString} from "query-string";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Text, Tooltip, XAxis, YAxis,} from "recharts";

import topPerformer from "assets/Icon_Performer_Up.png";
import bottomPerformer from "assets/Icon_Performer_Down.png";
import commentAnalysisIcon from "assets/Icon_Tab_03.png";
import trendingKeywordsIcon from "assets/Icon_Tab_11.png";
import staffMentionsIcon from "assets/Icon_Tab_14.png";
import contactReportIcon from "assets/Icon_Tab_12.png";
import downloadsIcon from "assets/Icon_Tab_13.png";
import leagueTableIcon from "assets/Icon_Tab_04.png";
import logInsIcon from "assets/Icon_Tab_05.png";

import {dateFormatter} from "components/helpers/dateFormatter/dateFormatter";
import {initialsGrabber} from "components/helpers/initialsGrabber/initialsGrabber";
import {viewResource} from "components/helpers/resources/viewResource";
import {downloadResource} from "components/helpers/resources/downloadResource";
import {useAsyncFunction} from "helpers/useAsyncFunction";
import classNames from "classnames";
import {useHideResize} from "helpers/useHideResize";
import {useDispatch, useSelector} from "react-redux";
import QuickFilterBar from "components/QuickFilterBar";
import {FilterDetails, GeneralFiltersDetails} from "helpers/useGetFilterDetails";
import {LeagueTable} from "components/LeagueTables/LeagueTables";
import Frame from "components/Frame";
import {useReactToPrint} from "react-to-print";
import LogoLight from "../../assets/Logo_Light.png";
import {closePrint, selectPrint} from "../../reducers/print";
import {PortalFields} from "../LeftMenu/LeftMenuLinks/models";
import {hideFilterMenu} from "../../reducers/filterMenu";
import {removeLeftMenu} from "../../reducers/leftMenu";
import {hideNavHeader} from "../../reducers/navHeader";


type DashboardConfigurationItem = {
    w: number;
    h: number;
    x: number;
    y: number;
    i: number;
    fw: boolean;
    context: {
        componentType: string;
        [k: string]: any;
    };
};
type DashboardConfiguration = DashboardConfigurationItem[];

const url = endpoints.dashboard.getDashboard;
const resourceUrl = endpoints.fileModule.downloadFile;

const hiddenColumnsDownloads = ["id", "link"];
const hiddenColumnsStaffMentions = ["reviewFeedFilterString"];

function rowFormatterDownloads(row: any) {
    return {
        ...row,
        date:
            row.date == null ? "---" : <div className="GreyTxt">{dateFormatter(row.date, "DATE")}</div>,
        cycle: row.cycle == null ? "---" : row.cycle,
        cycleName: row.cycleName == null ? "---" : row.cycleName,
    };
}

function rowFormatterLogins(row: any) {
    return {
        ...row,
        fullName:
            row.fullName == null ? (
                "---"
            ) : (
                <div className="TableCellFlex">
                    <div className="TableDot">
                        <div className="UserInitials">{initialsGrabber(row.fullName)}</div>
                    </div>
                    <div>{row.fullName}</div>
                </div>
            ),
        lastLogin:
            row.lastLogin == null ? (
                "---"
            ) : (
                <div className="GreyTxt">{dateFormatter(row.lastLogin, "DATETIME")}</div>
            ),
    };
}

function commentAnalysisRowFormatter(row: any) {
    return {
        ...row,
        positive: row.positive == null ? "---" : <div className="GreyTxt">{row.positive}</div>,
        negative: row.negative == null ? "---" : <div className="GreyTxt">{row.negative}</div>,
        sentimentScore:
            row.sentimentScore == null ? (
                "---"
            ) : (
                <div className="Sentiment">{row.sentimentScore.toFixed(2)}</div>
            ),
    };
}

const contactReportOptions = [
    {
        value: "Google",
        label: "Google",
    },
    {
        value: "TripAdvisor",
        label: "Trip Advisor",
    },
    {
        value: "Facebook",
        label: "Facebook",
    },
    {
        value: "Booking",
        label: "Booking.com",
    },
];

function percentageFormatter(item: any) {
    return item + "%";
}

function DashboardItem(props: {
    item: DashboardConfigurationItem;
    projectId: number;
    children?: React.ReactNode;
    order: number;
}) {
    const {
        children,
        item: {w, h, x, y, fw},
    } = props;
    let gridStyle;
    if (fw) {
        gridStyle = css`
            flex-grow: ${w};
            grid-area: ${y + 1} / 1 / span ${h} / span ${w};
            order: ${props.order};
            min-width: 800px;
        `
    } else {
        gridStyle = css`
            flex-grow: ${w};
            grid-area: ${y + 1} / ${x + 1} / span ${h} / span ${w};
            order: ${props.order};
        `
    }
    return (
        <div
            css={gridStyle}
            className={classNames(styles.dashboard__item__content, styles.dashboard__item)}
        >
            {children}
        </div>
    );
}

export function getHrefFromItem(
    item: string,
    projectId: number,
    portalFields: PortalFields | null
): string | null {
    if (!portalFields) return null;
    const base = `/project/${projectId}/`;
    switch (item) {
        case "reviewCard":
            if (portalFields.bReviewFeedVisible) {
                return base + "review-feed";
            } else {
                return null;
            }
        case "commentAnalysisTable":
            if (portalFields.bCommentAnalysisVisible) {
                return base + "comment-analysis";
            } else {
                return null;
            }
        case "performersTable":
            if (portalFields.bLeagueTablesVisible) {
                return base + "league-tables";
            } else {
                return null;
            }
        case "keyQuestionsTable":
            if (portalFields.bLeagueTablesVisible) {
                return base + "league-tables";
            } else {
                return null;
            }
        case "trendScoresChart":
            if (portalFields.bLeagueTablesVisible) {
                return base + "league-tables";
            } else {
                return null;
            }
        case "trendingKeywordsTable":
            if (portalFields.bCommentAnalysisVisible) {
                return base + "comment-analysis";
            } else {
                return null;
            }
        case "staffMentionsTable":
            if (portalFields.bReviewFeedVisible) {
                return base + "review-feed";
            } else {
                return null;
            }
        case "contactReportChart":
            if (portalFields.bLeagueTablesVisible) {
                return base + "league-tables";
            } else {
                return null;
            }
        case "leagueTable":
            if (portalFields.bLeagueTablesVisible) {
                return base + "league-tables";
            } else {
                return null;
            }
        case "summaryOfReviewsChart":
            if (portalFields.bReviewFeedVisible) {
                return base + "review-feed";
            } else {
                return null;
            }
        case "downloadsTable":
            if (portalFields.bDownloadsVisible) {
                return base + "downloads";
            } else {
                return null;
            }
        case "loginsTable":
            if (portalFields.bLoginReportVisible) {
                return base + "login-report";
            } else {
                return null;
            }
        default:
            return null;
    }
}

export const Tick: React.FC<any> = ({
                                        payload: {value},
                                        verticalAnchor,
                                        visibleTicksCount,
                                        ...rest
                                    }) => (
    <Text {...rest} verticalAnchor={verticalAnchor} visibleTicksCount={visibleTicksCount} width={200}>
        {value}
    </Text>
);

const ticks10 = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

type Props = {};

type Values = {
    navColour: string;
    posColour: string;
    negColour: string;
    mixedColour: string;
    neutralColour: string;
};

const initialValues = {
    posColour: "",
    negColour: "",
    mixedColour: "",
    neutralColour: "",
    navColour: ""
};

function getInitialValues(value?: string): Values {
    try {
        if (value) return JSON.parse(value);
        return initialValues;
    } catch (_err) {
        return initialValues;
    }
}

/** DASHBOARD STARTS */
const parseAndOrder = (config: string): DashboardConfiguration | null => {
    try {
        const parsed: DashboardConfiguration = JSON.parse(config);
        return parsed.sort((a, b) => {
            if (a.y < b.y) {
                return -1;
            }
            return 1;
        });
    } catch {
        return null;
    }
};

const Print: React.FC<Props> = (props) => {
    const {projectId} = useParams();
    let location = useLocation();
    let navigate = useNavigate();
    const databaseName = useSelector(selectDatabaseName);
    const portalConfig = useSelector(selectPortalConfig);
    const commentAnalysisConfig = getInitialValues(portalConfig?.CommentAnalysisConfiguration);
    const generalPortalCofig = getInitialValues(portalConfig?.GeneralPortalConfiguration);
    const portalData = useSelector(selectPortalData);
    const [itemRowPageArray, setItemRowPageArray] = React.useState<any[]>([[]]);
    const [dashboardData, setDashboardData] = React.useState<{ details: { [key: string]: any }, metadata: any }>();
    const [filterDetails, setFilterDetails] = React.useState<FilterDetails>();


    const dashboard: DashboardConfiguration | null = React.useMemo(() => {
        if (!portalConfig) return null;
        try {
            return parseAndOrder(portalConfig.DashboardConfiguration);
        } catch (_err) {
            return [];
        }
    }, [portalConfig]);

    const getDashboardData = useCallback(function () {
        const sData = window.sessionStorage.getItem("dashboard-details");
        const sMetaData = window.sessionStorage.getItem("dashboard-metadata");
        const sFilterDetails = window.sessionStorage.getItem("dashboard-filters");
        if (sFilterDetails !== null) {
            setFilterDetails(JSON.parse(sFilterDetails));
        }

        let details: { [key: string]: any };
        details = {};
        if (sData != null) {
            details = JSON.parse(sData);
        }
        let metadata: {
            filterString: string
        };

        if (sMetaData != null) {
            metadata = JSON.parse(sMetaData);
        } else {
            metadata = {
                filterString: ""
            };
        }

        // Find relevent elem number and append as a field
        // elem0 etc
        let rowNumbers: Set<number> = new Set();
        const itemRowToPage: any[] = []
        if (dashboard) {
            for (const [key, value] of Object.entries(details)) {
                // Find the integer at the end of Elem and use as an index to get the relevant positional data
                // from the dashboard config object
                let positionIndex = parseInt(key.replace("elem", ""));
                details[key].positionData = dashboard[positionIndex];
                let extraRows = 0;
                // Is this in the set already and one of the special types of chart?


                if (["keyQuestionsTable", "summaryOfReviewsChart", "trendingKeywordsTable", "trendScoresChart"].includes(details[key].positionData.context.componentType)) {
                    // add an extra y
                    details[key].positionData.fw = true;
                    details[key].positionData.w = 4;
                    details[key].positionData.y += extraRows;
                    // Get next number
                    let continueLoop = false;

                    let nextRow = details[key].positionData.y;
                    while (rowNumbers.has(nextRow)) {
                        nextRow += 1;
                        extraRows += 1;
                    }
                    details[key].positionData.y = nextRow;
                } else {
                    details[key].positionData.fw = false;
                }
                details[key].positionData.h= 1;
                rowNumbers.add(details[key].positionData.y);

            }
            const rowNumberArray: number[]  = Array.from(rowNumbers);
            rowNumberArray.sort((n1,n2) => n1 - n2)
            while (rowNumberArray.length > 0) {
                const chunk = rowNumberArray.splice(0, 2);
                itemRowToPage.push(chunk);
            }
            setItemRowPageArray(itemRowToPage);
            setDashboardData({details: details, metadata: metadata});
        }

    }, [dashboard])

    React.useEffect(() => {
        if (!dashboardData) {
            getDashboardData()
        }
    }, [dashboard, dashboardData])


    const componentRef = React.useRef<HTMLDivElement>(null);

    const [bind, resizing] = useHideResize();

    const [contactReportOption, setContactReportOption] = useState<SelectOption>(
        contactReportOptions[0]
    );
    const [visibleOptionsMenu, setVisibleOptionsMenu] = React.useState<number | null>(null);

    // If there's a filterstring in the query, grab it and use it as the initial filter.
    let {fs} = parseQueryString(location?.search ?? "");
    if (typeof fs !== "string") fs = null;
    const [filters, setFilters] = useState<string>(fs ?? "");
    const oldFilters = usePrevious(filters);

    const [loading, setLoading] = React.useState(false);

    const gridClassnames = classNames(
        styles.dashboard__grid,
        {MenuCollapsed: true},
        {MenuOpen: false}
    );
    const gridClassnamesWithoutPageBreak = classNames(
        styles.dashboard__grid__without__page__break,
        {MenuCollapsed: true},
        {MenuOpen: false}
    );


    function usePrevious<T>(
        value: T,
    ): MutableRefObject<T | undefined>['current'] {
        const ref = React.useRef<T>();
        React.useEffect(() => {
            ref.current = value;
        }, [value]);
        return ref.current;
    }


    const handleContactReportOptionSelect = (selectedOption: SelectOption) => {
        setContactReportOption(selectedOption);
    };


    // const [images, setImages] = React.useState<ImageMap>({});
    const [displayPrint, setDisplayPrint] = React.useState(false);


    const content = React.useMemo(() => {
        if (!dashboardData) {
            return <Spinner/>;
        }


        const dashboardItemContent = (item: DashboardConfigurationItem, idx: number) => {

            const dataName = "elem" + String(idx);
            const data = dashboardData.details[dataName];

            if (dashboardData) {
                switch (item.context.componentType) {
                    case "reviewCard":
                        return (
                            <div>
                                {data && (
                                    <SummaryCard
                                        href={getHrefFromItem(
                                            item.context.componentType,
                                            parseInt(projectId ?? "0", 10),
                                            portalData
                                        )}
                                        site={item.context.values.reviewType}
                                        reviews={data.totalRatings}
                                        breakdown={data.scores}
                                    />
                                )}
                            </div>
                        );

                    case "commentAnalysisTable":
                        return (
                            <div>
                                <Card tableCard={true}>
                                    <div>
                                        <DataTable
                                            table={{
                                                data: {
                                                    details: data,
                                                    actions: {},
                                                },
                                            }}
                                            notSortable={true}
                                            href={getHrefFromItem(
                                                item.context.componentType,
                                                parseInt(projectId ?? "0", 10),
                                                portalData
                                            )}
                                            rowFormatter={commentAnalysisRowFormatter}
                                            dashboardTable={true}
                                            icon={commentAnalysisIcon}
                                            title="COMMENT ANALYSIS"
                                        />
                                    </div>
                                </Card>
                            </div>
                        );

                    case "performersTable":
                        if (data && data.length < 2) {
                            // ‘Performerstable’ to be hidden when count <=1
                            return <Fragment/>
                        }
                        if (item.context.values.topOrBottom === "bottom") {
                            return (
                                <div>
                                    <div>
                                        {data && (
                                            <Card
                                                href={getHrefFromItem(
                                                    item.context.componentType,
                                                    parseInt(projectId ?? "0", 10),
                                                    portalData
                                                )}
                                                icon={bottomPerformer}
                                                title={
                                                    item.context.values.customChartName ??
                                                    `BOTTOM ${data.length ? data.length : ""} PERFORMERS - ${item.context.values.question.toUpperCase()}`
                                                }
                                            >
                                                <div>
                                                    <table
                                                        className="CardTable"
                                                        style={{
                                                            borderBottom: "1px solid #e4e7f0",
                                                            borderLeft: "1px solid #e4e7f0",
                                                            borderRight: "1px solid #e4e7f0",
                                                        }}
                                                    >
                                                        <tbody>
                                                        {data &&
                                                            data.map((p: {
                                                                name: string;
                                                                score: number
                                                            }, id: number) => (
                                                                <tr className="CardTableRow" key={id}>
                                                                    <td className="CardTableCell">
                                                                        <div className="CardTableItemSpaced">
                                                                            <div>{p.name}</div>
                                                                            <div className="BottomHighlight">
                                                                                {(p.score * 100).toFixed(1)}%
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Card>
                                        )}
                                    </div>
                                </div>
                            );
                        } else {
                            return (
                                <div>
                                    <div>
                                        {data && (
                                            <Card
                                                href={getHrefFromItem(
                                                    item.context.componentType,
                                                    parseInt(projectId ?? "0", 10),
                                                    portalData
                                                )}
                                                icon={topPerformer}
                                                title={
                                                    item.context.values.customChartName ??
                                                    `TOP ${data.length ? data.length : ""} PERFORMERS - ${item.context.values.question.toUpperCase()}`
                                                }
                                            >
                                                <div>
                                                    <table
                                                        className="CardTable"
                                                        style={{
                                                            borderBottom: "1px solid #e4e7f0",
                                                            borderLeft: "1px solid #e4e7f0",
                                                            borderRight: "1px solid #e4e7f0",
                                                        }}
                                                    >
                                                        <tbody>
                                                        {(() => {
                                                            return (
                                                                data &&
                                                                Array.isArray(data) &&
                                                                data.map((p: {
                                                                    name: string;
                                                                    score: number
                                                                }, id: number) => {
                                                                    return (
                                                                        <tr className="CardTableRow" key={id}>
                                                                            <td className="CardTableCell">
                                                                                <div className="CardTableItemSpaced">
                                                                                    <div>{p.name}</div>
                                                                                    <div className="TopHighlight">
                                                                                        {(p.score * 100).toFixed(1)}%
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            );
                                                        })()}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Card>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                    case "keyQuestionsTable": {
                        let keyQuestionsMinWidth = "800px";

                        return (
                            <div style={{"minWidth": "800px"}}>
                                <KeyQuestions
                                    href={getHrefFromItem(
                                        item.context.componentType,
                                        parseInt(projectId ?? "0", 10),
                                        portalData
                                    )}
                                    data={data}
                                    minWidth={keyQuestionsMinWidth}
                                    defaultChartType={item.context.values.defaultChartType}
                                    customName={item.context.values.customChartName}
                                />
                            </div>
                        );
                    }

                    case "trendScoresChart":
                        // We only want the most recent 8 data points or it will print with a scroll
                        if (data && data.pieChartData && data.otherChartData && data.otherChartData.length > 1) {
                            for (let i = 0; i < data.pieChartData.data.length; i++) {
                                if (data.pieChartData.data.length > 8) {
                                    data.pieChartData.data.shift();
                                    data.pieChartData.periods.shift();
                                    data.otherChartData.shift();
                                }
                            }

                            return (
                                <div>
                                    <TrendScores
                                        href={getHrefFromItem(
                                            item.context.componentType,
                                            parseInt(projectId ?? "0", 10),
                                            portalData
                                        )}
                                        data={data}
                                        defaultChartType={item.context.values.defaultChartType}
                                        questions={item.context.values.questions}
                                        customName={item.context.values.customChartName}
                                    />
                                </div>
                            );
                        } else {
                            return null;
                        }


                    case "trendingKeywordsTable":
                        return (
                            <div style={{overflow: "hidden"}}>
                                <Card
                                    icon={trendingKeywordsIcon}
                                    title="TRENDING KEYWORDS"
                                    href={getHrefFromItem(
                                        item.context.componentType,
                                        parseInt(projectId ?? "0", 10),
                                        portalData
                                    )}
                                >
                                    <div className="CardBody">
                                        <div
                                            className="TrendingKeywordRow VeryPositive"
                                            style={{
                                                color: commentAnalysisConfig?.posColour
                                                    ? commentAnalysisConfig.posColour
                                                    : undefined,
                                            }}
                                        >
                                            <div className="TrendingCategory">POSITIVE</div>
                                            {data &&
                                                data.positive &&
                                                data.positive.map((p: { keyword: string; count: number }) => (
                                                    <React.Fragment key={p.keyword}>
                                                        <div className="TrendingKeyword">{p.keyword}</div>
                                                        <div className="TrendingCount">({p.count})</div>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                        <div
                                            className="TrendingKeywordRow Positive"
                                            style={{
                                                color: commentAnalysisConfig?.mixedColour
                                                    ? commentAnalysisConfig.mixedColour
                                                    : undefined,
                                            }}
                                        >
                                            <div className="TrendingCategory">MIXED</div>
                                            {data &&
                                                data.mixed &&
                                                data.mixed.map((p: { keyword: string; count: number }) => (
                                                    <React.Fragment key={p.keyword}>
                                                        <div className="TrendingKeyword">{p.keyword}</div>
                                                        <div className="TrendingCount">({p.count})</div>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                        <div
                                            className="TrendingKeywordRow GreyTxt"
                                            style={{
                                                color: commentAnalysisConfig?.neutralColour
                                                    ? commentAnalysisConfig.neutralColour
                                                    : undefined,
                                            }}
                                        >
                                            <div className="TrendingCategory">NEUTRAL</div>
                                            {data &&
                                                data.neutral &&
                                                data.neutral.map((p: { keyword: string; count: number }) => (
                                                    <React.Fragment key={p.keyword}>
                                                        <div className="TrendingKeyword">{p.keyword}</div>
                                                        <div className="TrendingCount">({p.count})</div>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                        <div
                                            className="TrendingKeywordRow Negative"
                                            style={{
                                                color: commentAnalysisConfig?.negColour
                                                    ? commentAnalysisConfig.negColour
                                                    : undefined,
                                            }}
                                        >
                                            <div className="TrendingCategory">NEGATIVE</div>
                                            {data &&
                                                data.negative &&
                                                data.negative.map((p: { keyword: string; count: number }) => (
                                                    <React.Fragment key={p.keyword}>
                                                        <div className="TrendingKeyword">{p.keyword}</div>
                                                        <div className="TrendingCount">({p.count})</div>
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        );

                    case "staffMentionsTable":
                        return (
                            <div>
                                <Card tableCard={true}>
                                    <div className="CardBody">
                                        <DataTable
                                            table={{
                                                data: {
                                                    details: data,
                                                    actions: {},
                                                },
                                            }}
                                            hiddenColumns={hiddenColumnsStaffMentions}
                                            notSortable={true}
                                            dashboardTable={true}
                                            href={getHrefFromItem(
                                                item.context.componentType,
                                                parseInt(projectId ?? "0", 10),
                                                portalData
                                            )}
                                            icon={staffMentionsIcon}
                                            title="STAFF MENTIONS"
                                            customColWidth={{
                                                column: 2,
                                                width: {minWidth: "400px", flex: "400px"},
                                            }}
                                            rowOptionComponent={
                                                <OptionRowTable
                                                    table={
                                                        {
                                                            data: {
                                                                details: data,
                                                                actions: {},
                                                            },
                                                        } as any
                                                    }
                                                    visibleOptionsMenu={visibleOptionsMenu}
                                                    setVisibleOptionsMenu={setVisibleOptionsMenu}
                                                    dashboardTable={true}
                                                    children={(data: any) => {
                                                        return (
                                                            <React.Fragment>
                                                                <button
                                                                    className="Option"
                                                                    onClick={() => {
                                                                        navigate(
                                                                            `/project/${projectId}/review-feed?fs=${data.reviewFeedFilterString}`
                                                                        );
                                                                    }}
                                                                >
                                                                    View review
                                                                </button>
                                                            </React.Fragment>
                                                        );
                                                    }}
                                                />
                                            }
                                        />
                                    </div>
                                </Card>
                            </div>
                        );

                    case "contactReportChart":
                        return (
                            <div>
                                {data && (
                                    <Card
                                        icon={contactReportIcon}
                                        title="CONTACT REPORT"
                                        href={getHrefFromItem(
                                            item.context.componentType,
                                            parseInt(projectId ?? "0", 10),
                                            portalData
                                        )}
                                    >

                                        <div className="CardBody">
                                            <div className="CategoryDropdown">
                                                <Dropdown
                                                    name="ContactReport"
                                                    value={contactReportOption}
                                                    options={contactReportOptions}
                                                    type="Category"
                                                    onChange={handleContactReportOptionSelect}
                                                />
                                            </div>
                                            {/* CONTACT REPORT CHART STARTS HERE */}
                                            <div className="Chart">
                                                <div className="ChartWrapper">
                                                    <div style={{minWidth: "1000px", display: "initial"}}>
                                                        <ResponsiveContainer width="100%" height={350}>
                                                            <BarChart
                                                                data={
                                                                    contactReportOption.value === "Google"
                                                                        ? data.Google
                                                                        : contactReportOption.value === "TripAdvisor"
                                                                            ? data.TripAdvisor
                                                                            : contactReportOption.value === "Facebook"
                                                                                ? data.Facebook
                                                                                : data.Booking
                                                                }
                                                                layout="vertical"
                                                                margin={{left: 30, right: 20, bottom: 10}}
                                                            >
                                                                <CartesianGrid horizontal={false} strokeDasharray="3"/>
                                                                <Tooltip
                                                                    allowEscapeViewBox={{x: true, y: true}}
                                                                    coordinate={{x: 0, y: 300}}
                                                                />
                                                                <XAxis
                                                                    type="number"
                                                                    axisLine={false}
                                                                    tickLine={false}
                                                                    tickFormatter={percentageFormatter}
                                                                    domain={[1, 100]}
                                                                    ticks={ticks10}
                                                                />
                                                                <YAxis
                                                                    dataKey="name"
                                                                    type="category"
                                                                    axisLine={false}
                                                                    tickLine={false}
                                                                    dx={-10}
                                                                />
                                                                <Legend iconType="circle" iconSize={8}/>
                                                                <Bar
                                                                    dataKey="zeroToTwoDays"
                                                                    name="0-2 days"
                                                                    stackId="y"
                                                                    fill="#8fba34"
                                                                    barSize={30}
                                                                    isAnimationActive={false}
                                                                />
                                                                <Bar
                                                                    dataKey="threeToSevenDays"
                                                                    name="3-7 days"
                                                                    stackId="y"
                                                                    fill="#fbbf22"
                                                                    barSize={30}
                                                                    isAnimationActive={false}
                                                                />
                                                                <Bar
                                                                    dataKey="moreThanSevenDays"
                                                                    name="7 days +"
                                                                    stackId="y"
                                                                    fill="#f8891b"
                                                                    barSize={30}
                                                                    isAnimationActive={false}
                                                                />
                                                                <Bar
                                                                    dataKey="noResponse"
                                                                    name="No response"
                                                                    stackId="y"
                                                                    fill="#d12a2a"
                                                                    barSize={30}
                                                                    isAnimationActive={false}
                                                                />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* CONTACT REPORT CHART ENDS HERE */}
                                        </div>
                                    </Card>
                                )}
                            </div>
                        );

                    case "leagueTable":
                        return (
                            <div>
                                <Card
                                    icon={leagueTableIcon}
                                    title="LEAGUE TABLE"
                                    tableCard={true}
                                    href={getHrefFromItem(
                                        item.context.componentType,
                                        parseInt(projectId ?? "0", 10),
                                        portalData
                                    )}
                                >
                                    {data?.LocationName && (
                                        <LeagueTable
                                            projectId={projectId ?? ""}
                                            table={data.LocationName}
                                            firstColName={"Name"}
                                        />
                                    )}
                                </Card>
                            </div>
                        );

                    case "summaryOfReviewsChart":
                        return (
                            <div>
                                <SummaryOfReviews
                                    href={getHrefFromItem(
                                        item.context.componentType,
                                        parseInt(projectId ?? "0", 10),
                                        portalData
                                    )}
                                    data={data}
                                    defaultChartType={item.context.values.defaultChartType}
                                    customName={item.context.values.customChartName}
                                />
                            </div>
                        );

                    case "downloadsTable":
                        return (
                            <div>
                                <Card tableCard={true}>
                                    <div className="CardBody">
                                        <DataTable
                                            table={{
                                                data: {
                                                    details: data,
                                                    actions: {},
                                                },
                                            }}
                                            notSortable={true}
                                            dashboardTable={true}
                                            href={getHrefFromItem(
                                                item.context.componentType,
                                                parseInt(projectId ?? "0", 10),
                                                portalData
                                            )}
                                            icon={downloadsIcon}
                                            title="DOWNLOADS"
                                            hiddenColumns={hiddenColumnsDownloads}
                                            rowFormatter={rowFormatterDownloads}
                                            rowOptionComponent={
                                                <OptionRowTable
                                                    table={
                                                        {
                                                            data: {
                                                                details: data,
                                                                actions: {},
                                                            },
                                                        } as any
                                                    }
                                                    visibleOptionsMenu={visibleOptionsMenu}
                                                    setVisibleOptionsMenu={setVisibleOptionsMenu}
                                                    dashboardTable={true}
                                                    children={(data: any) => {
                                                        return (
                                                            <React.Fragment>
                                                                {data.type === ".doc" || data.type === ".docx" ? null : (
                                                                    <button
                                                                        className="Option"
                                                                        onClick={() => viewResource(resourceUrl, data)}
                                                                    >
                                                                        View
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="Option"
                                                                    onClick={() => downloadResource(resourceUrl, data)}
                                                                >
                                                                    Download
                                                                </button>
                                                            </React.Fragment>
                                                        );
                                                    }}
                                                />
                                            }
                                        />
                                    </div>
                                </Card>
                            </div>
                        );

                    case "loginsTable":
                        return (
                            <div>
                                <Card tableCard={true}>
                                    <div className="CardBody">
                                        <DataTable
                                            table={{
                                                data: {
                                                    details: data,
                                                    actions: {},
                                                },
                                            }}
                                            notSortable={true}
                                            rowFormatter={rowFormatterLogins}
                                            dashboardTable={true}
                                            icon={logInsIcon}
                                            href={getHrefFromItem(
                                                item.context.componentType,
                                                parseInt(projectId ?? "0", 10),
                                                portalData
                                            )}
                                            title="LOG IN"
                                        />
                                    </div>
                                </Card>
                            </div>
                        );

                    default:
                        return null;
                }
            }
        };
        return (
            <div id="Print">
                {resizing || !dashboard || !dashboardData ? (
                    <div>
                        <h1>LOADING</h1>
                        <Spinner/>
                    </div>
                ) : (
                    <React.Fragment>
dfsgsdfgdsfg
                        {dashboardData?.metadata?.filterString && dashboardData.metadata.filterString !== "" ? (
                            filterDetails && filterDetails.data ? (
                                <QuickFilterBar
                                    filterString={dashboardData.metadata.filterString}
                                    setFilters={setFilters}
                                    filterDetails={filterDetails}
                                    noWidthContent
                                />
                            ) : (
                                <div style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#f8f9fa",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "4px",
                                    margin: "10px 0"
                                }}>
                                    <span style={{ fontWeight: "bold" }}>Filters: </span>
                                    <span>
                                        {dashboardData.metadata.filterString
                                            .split("||")
                                            .map((filter: { split: (arg0: string) => [any, any]; }) => {
                                                const [key, value] = filter.split("::");
                                                return `${key}: ${value}`;
                                            })
                                            .join(", ")
                                        }
                                    </span>
                                </div>
                            )
                        ) : <br />}
                        {itemRowPageArray.map((x, i) => {
                            return (
                                <div {...bind} key={"page" + i.toString()} className={(i == itemRowPageArray.length -1 ? gridClassnamesWithoutPageBreak : gridClassnames)}>
                                    {Object.entries(dashboard).map(([key, value], j) => {
                                        //elem0, elem1 etc
                                        let gridItem: DashboardConfigurationItem = value;
                                        if (!gridItem) {
                                            return <h1>Not Found</h1>
                                        }

                                        // If page is 0 then we want rows which are 0,1... page 1 - rows 2,3
                                        // Got to be a better way of calculating this
                                        if (x.includes(gridItem.y)) {
                                            let dbItem = dashboardItemContent(gridItem, j);


                                            if (dbItem) {

                                                return (
                                                    <DashboardItem
                                                        projectId={projectId ? parseInt(projectId, 10) : 0}
                                                        key={j}
                                                        item={gridItem}
                                                        order={j}
                                                    >
                                                        {dbItem}

                                                    </DashboardItem>
                                                )
                                            }
                                        }
                                    })}

                                </div>)
                        })}
                    </React.Fragment>
                )}
            </div>
        );
    }, [dashboard, dashboardData, resizing]); // Possibly needs updating with more dependencies

    const dispatch = useDispatch();
    dispatch(hideFilterMenu());
    dispatch(removeLeftMenu());
    // dispatch(hideNavHeader());

    const reactToPrint = useReactToPrint({
        content: () => componentRef.current,
        onBeforeGetContent: () => {
            return new Promise(resolve => setTimeout(resolve, 1000))
        },
        onBeforePrint: () => {
            return new Promise(resolve => setTimeout(resolve, 1000))
        },
        onAfterPrint: () => {
            setDisplayPrint(false);
            dispatch(closePrint());
        },
        onPrintError: () => {
            setDisplayPrint(false);
            dispatch(closePrint());
        }
    });

    const clientLogoLink = useSelector(selectClientLogoLink);
    const fetchClientLogo = React.useCallback(async () => {
        const queries = parseQueryString(clientLogoLink);
        const {fileextension} = queries;
        const res = await api.get(clientLogoLink, {responseType: "arraybuffer"});
        const asUint8Array = new Uint8Array(res.data);
        const blob = new Blob([asUint8Array], {type: `image/${fileextension?.slice(1) ?? "jpeg"}`});
        return URL.createObjectURL(blob);
    }, [clientLogoLink]);

    const clientLogo = useAsyncFunction(fetchClientLogo);
    const mainClassName = `WidthContent ReviewFeed ${
        filters ? "NoTopHeightContent" : "HeightContent"
    }`;
    const printPage = () => {
        setDisplayPrint(true);
        if (reactToPrint) {
            reactToPrint();
        }
    }

    const print = useSelector(selectPrint);

    useEffect(() => {
        if (!loading) {
            printPage();
        }
    }, [loading])


    return (
        <Frame
            portalConfigField="bDashboardVisible"
            userAccessField="bCanAccessDashboard"
        >
            {loading ? <Spinner/> :
                <div style={{"width": "800px", "display": "flex", "borderWidth": "2px", "borderColor": "black"}}>
                    <div ref={componentRef}>
                        <div className="NavHeader" style={{"position": "static",
                            backgroundColor: generalPortalCofig?.navColour
                                ? generalPortalCofig.navColour
                                : undefined,}}>
                            <img
                                className="ServiceMonitorLogo"
                                src={LogoLight}
                                alt="Service Monitor logo"
                                style={{
                                    "maxWidth": "25%",
                                    "paddingLeft": 20
                                }}
                            />
                            <div style={{
                                "maxWidth": "25%", "maxHeight": "60px"
                            }}>
                                {clientLogo.data && <img className="ServiceMonitorLogo"
                                                         src={clientLogo.data} alt="Brand logo" style={{"maxHeight": "40px", "paddingRight": "20px"}}/>}
                            </div>
                        </div>
                        {content}
                    </div>
                </div>}
        </Frame>
    );
};

export default Print;
